import React, { Component, ErrorInfo, ReactNode } from 'react';
import { ErrorCircleRegular } from '@fluentui/react-icons';

interface ErrorBoundaryProps {
  children: ReactNode;
  fallbackContent?: ReactNode;
  errorData?: any;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error?: Error | null;
}

/**
 * Error Boundary component to catch JavaScript errors anywhere in the child component tree
 * and display a fallback UI instead of crashing the whole component tree.
 */
class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    // Update state so the next render shows the fallback UI
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    // You can log the error to an error reporting service
    console.error('Error caught by ErrorBoundary:', error, errorInfo);
  }

  render(): ReactNode {
    if (this.state.hasError) {
      // If a custom fallback was provided, use it
      if (this.props.fallbackContent) {
        return this.props.fallbackContent;
      }

      // Default error UI
      return (
        <div className="error-boundary-fallback" style={{ display: 'flex', alignItems: 'center' }}>
          <ErrorCircleRegular style={{ color: '#d13438', marginRight: '8px' }} />
          <span>Something went wrong while rendering this content.</span>
          {this.props.errorData && typeof this.props.errorData.analysisText === 'string' && (
            <pre style={{ 
              marginTop: '10px', 
              padding: '8px', 
              backgroundColor: '#f9f9f9', 
              borderRadius: '4px', 
              maxHeight: '200px', 
              overflow: 'auto' 
            }}>
              {this.props.errorData.analysisText.substring(0, 500) + 
                (this.props.errorData.analysisText.length > 500 ? '...' : '')}
            </pre>
          )}
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
